import React, { memo, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardSizesEnum, CartItemSummary, PopoverMenu, ServiceIcons, ViewportContext } from "@monorepo/components";
import { routeConfig } from "@monorepo/constants/routeConfig";
import { DeleteIcon } from "@monorepo/icons";
import {
  useDelayPaymentMutation,
  useGetCartGroupByIdQuery,
  useUndelayPaymentMutation,
} from "@monorepo/services/api/carts";
import theme from "@monorepo/styles/theme";
import { CartStatusesEnum, DelayStatusesEnum, ServiceCategoryIdEnum } from "@monorepo/types";
import { MoreVert } from "@mui/icons-material";
import PauseCircleOutlineRoundedIcon from "@mui/icons-material/PauseCircleOutlineRounded";
import PlayCircleOutlineRoundedIcon from "@mui/icons-material/PlayCircleOutlineRounded";
import { Box, IconButton } from "@mui/material";
import { Popover } from "@mui/material";
import Button from "@mui/material/Button";
import { reverse } from "named-urls";

import { StyledLink } from "./CartGroupSummaryItem.styles";

interface ICartGroupSummaryItemProps {
  id: string;
  sum: string;
  title: string;
  delayed: DelayStatusesEnum;
  statusId: CartStatusesEnum;
  cartGroupId: string;
  categoryId: ServiceCategoryIdEnum;
  onDelete: ({ id, name }: { id: string; name: string }) => void;
}

export const CartGroupSummaryItem: React.FC<ICartGroupSummaryItemProps> = memo(
  ({ id, sum, title, delayed, cartGroupId, statusId, categoryId, onDelete }) => {
    const { t } = useTranslation();
    const paid = statusId === CartStatusesEnum.PAID;
    const itemDelayed = delayed === DelayStatusesEnum.DELAYED;
    const { isMobile } = useContext(ViewportContext);
    const [showPopoverMenu, setShowPopoverMenu] = useState<boolean>(false);
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

    const [delayPayment, { isLoading: delayPaymentIsLoading }] = useDelayPaymentMutation();
    const [undelayPayment, { isLoading: undelayPaymentIsLoading }] = useUndelayPaymentMutation();
    const { data: cartGroup } = useGetCartGroupByIdQuery(cartGroupId, { skip: !cartGroupId });
    const hasDeletePermissions = cartGroup?.is_owner || Boolean(cartGroup?.delete);
    const path = reverse(routeConfig.dashboard.payments.cart.cartId, {
      cartId: id,
    });

    const handleClickMore = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setAnchorEl(event.currentTarget);
      setShowPopoverMenu(true);
    };

    const handleCloseMore = (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      setShowPopoverMenu(false);
      setAnchorEl(null);
    };

    const actionIcon = useMemo(() => {
      if (statusId === CartStatusesEnum.STALE) {
        return null;
      } else {
        return delayed === DelayStatusesEnum.DELAYED ? (
          <PlayCircleOutlineRoundedIcon />
        ) : (
          <PauseCircleOutlineRoundedIcon />
        );
      }
    }, [statusId, delayed]);

    return (
      <Card
        sx={{
          width: {
            xs: "100%",
            sm: "100%",
            md: "485px",
            xl: "600px",
            xxl: "800px",
          },
          gridRowGap: {
            xs: theme.spacing(3),
            xl: "auto",
          },
          gridTemplateColumns: "1fr auto",
          color: delayed ? theme.palette.text.disabled : "inherit",
          gridColumnGap: paid && !itemDelayed ? 0 : theme.spacing(2),
        }}
        size={CardSizesEnum.SMALL}
      >
        <StyledLink to={path}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: paid ? "35px 1.2fr 0.2fr" : "35px 1.2fr 0.2fr",
              },
              gridColumnGap: theme.spacing(3),
              alignItems: "center",
            }}
          >
            <ServiceIcons
              active={delayed === DelayStatusesEnum.NON_DELAYED}
              serviceId={categoryId}
              statusId={statusId}
              delayed={delayed === DelayStatusesEnum.DELAYED}
            />
            <CartItemSummary title={title} sum={sum} delayed={delayed} statusId={statusId} />
          </Box>
        </StyledLink>
        <Box
          sx={{
            display: "flex",
            columnGap: "8px",
            justifyContent: "flex-end",
            alignItems: "center",
            paddingTop: {
              xs: theme.spacing(1),
              xl: theme.spacing(0),
            },
          }}
        >
          {!isMobile && (
            <>
              <Box
                sx={{
                  marginTop: "3px",
                  fontSize: theme.typography.caption.fontSize,
                  color: theme.palette.text.disabled,
                }}
              >
                {!paid && statusId !== CartStatusesEnum.DECLARED && statusId !== CartStatusesEnum.STALE && (
                  <Button
                    disableElevation
                    disabled={delayPaymentIsLoading || undelayPaymentIsLoading}
                    variant="outlined"
                    sx={{
                      padding: "10px 5px",
                      borderColor: delayed ? theme.palette.gullGray.main : theme.palette.primary.main,
                      color: delayed ? theme.palette.text.disabled : theme.palette.primary.main,
                      minWidth: "150px",
                    }}
                    onClick={() => {
                      if (delayed === DelayStatusesEnum.DELAYED) {
                        undelayPayment({ cartGroupId, id });
                      } else {
                        delayPayment({ cartGroupId, id });
                      }
                    }}
                  >
                    {delayed === DelayStatusesEnum.DELAYED ? t("payments.pay_now") : t("payments.pay_later")}
                  </Button>
                )}
              </Box>
              {hasDeletePermissions && (
                <IconButton aria-label={t("common.delete")} size="small" onClick={() => onDelete({ id, name: title })}>
                  <DeleteIcon sx={{ stroke: theme.palette.gullGray.main }} />
                </IconButton>
              )}
            </>
          )}
          {isMobile && (
            <>
              <IconButton aria-label={t("common.show_actions")} size="small" onClick={handleClickMore}>
                <MoreVert sx={{ color: theme.palette.text.disabled }} />
              </IconButton>
              <Popover
                anchorOrigin={{
                  vertical: "center",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "center",
                  horizontal: "left",
                }}
                open={showPopoverMenu}
                anchorEl={anchorEl}
                onClose={handleCloseMore}
              >
                <PopoverMenu
                  onAction={
                    statusId === CartStatusesEnum.STALE
                      ? undefined
                      : () => {
                          if (delayed === DelayStatusesEnum.DELAYED) {
                            undelayPayment({ cartGroupId, id });
                          } else {
                            delayPayment({ cartGroupId, id });
                          }
                          setShowPopoverMenu(false);
                        }
                  }
                  onDelete={() => {
                    onDelete({ id, name: title });
                    setShowPopoverMenu(false);
                  }}
                  actionIcon={actionIcon}
                />
              </Popover>
            </>
          )}
        </Box>
      </Card>
    );
  }
);
